import { useRouter } from "next/router";
import { ReactElement } from "react";

import { isConsultTimeWindow, useGetServerTime } from "@/hooks/useGetServerTime";

interface TimeSplitContainerProps {
  /** 야간에 렌더할 트리 */
  night: ReactElement;
  /** 주간에 렌더할 트리 */
  daytime: ReactElement;
}

/**
 * QA용 시간대 강제. `?force_time=day` 또는 `?force_time=night`.
 *
 * 실험이 시간대로 갈리면 반대 시간대는 확인할 방법이 없다. 밤에는 주간 실험을,
 * 낮에는 야간 실험을 볼 수 없어 QA가 시계에 묶인다.
 *
 * 환경 제한을 두지 않아 프로덕션에서도 동작한다. 고객이 이 쿼리를 직접 붙일 일은 없고,
 * 배포 후 실제 환경에서 분기를 확인해야 하는 경우가 있어서다.
 * 다만 이 경로로 들어가면 실험 컨테이너가 실제로 마운트되므로 Hackle에 노출이 남는다.
 * 실험이 돌고 있는 중에는 남용하지 않는다.
 *
 * @param forceTime - router.query.force_time (string | string[] | undefined)
 */
const parseForcedTime = (forceTime: string | string[] | undefined): "night" | "daytime" | null => {
  if (forceTime === "night") return "night";
  if (forceTime === "day") return "daytime";
  return null;
};

/**
 * 서버 시간(야간/주말/연휴 vs 주간) 기준으로 화면을 가르는 컨테이너.
 *
 * 실험이 시간대에 따라 갈릴 때 쓴다. 렌더되지 않은 쪽은 마운트되지 않으므로
 * 그 안의 실험 컨테이너도 variation을 호출하지 않고, 따라서 할당도 일어나지 않는다.
 * 실험 컨테이너를 모수일 때만 마운트한다는 원칙을 이 경계가 지킨다.
 *
 * 판정 전에는 아무것도 렌더하지 않는다.
 * 로딩 중 한쪽을 미리 보여주면 그 순간 실험이 마운트돼 잘못된 모수가 들어간다.
 * 기본값을 주간으로 잡으면 야간 사용자가 섞이고, 야간으로 잡으면 반대가 된다.
 *
 * 왜 컴포넌트로 빼는가
 * - 시간대 판정이 호출부마다 흩어지면 한쪽만 고치는 실수가 난다.
 *   실제로 #328에서 로그인만 야간을 막고 견적 대기 화면을 놓쳐,
 *   로그인에서 제외된 사용자가 다음 화면에서 할당되는 일이 있었다.
 * - 판정을 이 컴포넌트 하나로 모으면 두 화면의 기준이 어긋날 수 없다.
 *
 * 시간대가 1순위 축이고 status 같은 조건은 각 트리 안에서 2순위로 처리한다.
 *
 * @example
 * <TimeSplitContainer
 *   night={nightScreen}
 *   daytime={daytimeScreen}
 * />
 */
export const TimeSplitContainer = ({ night, daytime }: TimeSplitContainerProps) => {
  const router = useRouter();
  const { data: serverTime, isLoading } = useGetServerTime();

  const forcedTime = parseForcedTime(router.query.force_time);
  if (forcedTime) return forcedTime === "night" ? night : daytime;

  if (isLoading) return null;

  return isConsultTimeWindow(serverTime) ? night : daytime;
};
